import { useEffect, Suspense, lazy, useRef } from 'react';
import { Switch, Route, withRouter, useHistory } from 'react-router-dom';
import styled, { ThemeProvider, css } from 'styled-components';
import { theme } from '@src/style/variable';
import Modal from '@src/components/common/Modal';

import ResponsiveHeader from '@src/components/ResponsiveHeader';
import ResponsiveFooter from '@src/components/ResponsiveFooter';
import EmptyPage from '@src/routes/EmptyPage';

// context
import UserProvider from '@src/context/UserContext';

import ScrollToTop from './components/ScrollToTop';
import KakaoAuth from './routes/auth/kakao';
import NaverAuth from './routes/auth/naver';
import GoogleAuth from './routes/auth/google';

import './App.css';

const HomeComponent = lazy(() => import('@src/routes/Home'));
const LoginComponent = lazy(() => import('@src/routes/Login'));
const MypageComponent = lazy(() => import('@src/routes/Mypage'));
const NftComponent = lazy(() => import('@src/routes/Nft'));
const PrivacyComponent = lazy(() => import('@src/routes/Privacy'));
const TermsComponent = lazy(() => import('@src/routes/Terms'));
const NotFoundComponent = lazy(() => import('@src/routes/NotFound'));
const HeaderHidingRouter = ['/login', '/_o/kakao', '/_o/google', '/_o/naver'];
const FooterHidingRouter = ['/login', '/_o/kakao', '/_o/google', '/_o/naver'];

// auth middleware
const AuthMiddleWarePage = lazy(() => import('@src/routes/auth-middleware'));

const App = ({ location }) => {
  const appRef = useRef();
  const history = useHistory();

  useEffect(() => {
    const { Kakao } = window;
    if (Kakao && !Kakao.isInitialized())
      Kakao.init(process.env.REACT_APP_KAKAO_JAVASCRIPT_API_KEY);
  }, []);

  return (
    <UserProvider>
      <ThemeProvider theme={theme}>
        <AppInner
          className="App__inner"
          ref={appRef}
          bottomPadding={location.pathname.indexOf('/nft') === 0}
          showNftDockBar={/^\/nft(.*)\w/g.test(history.location.pathname)}
        >
          <ScrollToTop appInnerContainerRef={appRef} />
          {HeaderHidingRouter.indexOf(location.pathname) === -1 && (
            <ResponsiveHeader className="App__ResponsiveHeader" />
          )}
          <Suspense fallback={<EmptyPage />}>
            <Switch>
              <Route exact path="/" component={() => <HomeComponent />} />
              <Route exact path="/login" component={() => <LoginComponent />} />
              <Route exact path="/_o/kakao" component={() => <KakaoAuth />} />
              <Route exact path="/_o/naver" component={() => <NaverAuth />} />
              <Route exact path="/_o/google" component={() => <GoogleAuth />} />

              <Route exact path="/terms" component={() => <TermsComponent />} />
              <Route
                exact
                path="/privacy"
                component={() => <PrivacyComponent />}
              />
              {/* 2022-08-16 - tabType을 URL로 받을 수 있도록 수정 */}
              <Route
                exact
                path={[
                  '/user',
                  '/user/:userId',
                  '/user/:userId/:tabType(sell|owned)',
                ]}
                component={({ match }) => <MypageComponent {...match.params} />}
              />
              {/* mypage로 이동을 위한 신규 Router 추가 */}
              <Route
                exact
                path="/nft/my"
                render={() => (
                  <AuthMiddleWarePage
                    replaceUrlAfterAuthorizing={`/login?login_return_url=${encodeURIComponent(
                      '/user',
                    )}&is_login=true`}
                  />
                )}
              />
              <Route
                exact
                path="/nft/:id"
                component={({ match }) => (
                  <NftComponent
                    id={match.params?.id}
                    AppInnerContainer={appRef.current}
                  />
                )}
              />
              <Route
                path={['*', '404']}
                component={() => <NotFoundComponent />}
              />
            </Switch>
          </Suspense>
          {FooterHidingRouter.indexOf(location.pathname) === -1 && (
            <ResponsiveFooter />
          )}
          <Suspense fallback={null}>
            <Modal container={appRef} />
          </Suspense>
        </AppInner>
      </ThemeProvider>
    </UserProvider>
  );
};

const AppInner = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: auto;

  ${(props) =>
    !props.showNftDockBar &&
    css`
      .Nft__DockBar {
        display: none;
      }
    `}
`;
export default withRouter(App);
